import { useEffect } from 'react'
import { useSetRecoilState } from 'recoil'
import eventBus from '@/services/eventBus'
import recoilStateUpdaters from '@/recoil/recoil.stateUpdaters.ts'
import enumsAtom from '@/pages/MDM/atoms/enums.atom.ts'
import unitsAtom from '@/pages/MDM/atoms/units.atom.ts'
import unitInstancesAtom from '@/pages/MDM/atoms/units.instances.atom.ts'
import passportSchemasAtom, { IPassportSchemasAtomState } from '@/pages/MDM/atoms/passportSchemas.atom.ts'
import { produce } from 'immer'

const useMDMEventsSubscriptions = () => {
  const setEnumsState = useSetRecoilState(enumsAtom)
  const setUnitsState = useSetRecoilState(unitsAtom)
  const setUnitInstancesState = useSetRecoilState(unitInstancesAtom)
  const setPassportsState = useSetRecoilState(passportSchemasAtom)

  useEffect(() => {
    const createEnumsSub = eventBus.sub('mdm.enums.created', (item) => {
      recoilStateUpdaters.itemCreated(setEnumsState, item)
    })

    const updateEnumsSub = eventBus.sub('mdm.enums.updated', (item) => {
      recoilStateUpdaters.itemUpdated(setEnumsState, item)
    })

    const removeEnumsSub = eventBus.sub('mdm.enums.deleted', (id) => {
      recoilStateUpdaters.itemDeleted(setEnumsState, id)
    })

    const createUnitsSub = eventBus.sub('mdm.units.created', (item) => {
      recoilStateUpdaters.itemCreated(setUnitsState, item)
    })

    const updateUnitsSub = eventBus.sub('mdm.units.updated', (item) => {
      recoilStateUpdaters.itemUpdated(setUnitsState, item)
    })

    const removeUnitsSub = eventBus.sub('mdm.units.deleted', (id) => {
      recoilStateUpdaters.itemDeleted(setUnitsState, id)
    })

    const createUnitInstanceSub = eventBus.sub('mdm.units.instances.created', (item) => {
      recoilStateUpdaters.itemCreated(setUnitInstancesState, item)
    })

    const updateUnitInstanceSub = eventBus.sub('mdm.units.instances.updated', (item) => {
      recoilStateUpdaters.itemUpdated(setUnitInstancesState, item)
    })

    const removeUnitInstanceSub = eventBus.sub('mdm.units.instances.deleted', (id) => {
      recoilStateUpdaters.itemDeleted(setUnitInstancesState, id)
    })

    const createPassportSchemaSub = eventBus.sub('mdm.passports.created', (item) => {
      recoilStateUpdaters.itemCreated(setPassportsState, item)
    })

    const updatePassportSchemaSub = eventBus.sub('mdm.passports.updated', (item) => {
      recoilStateUpdaters.itemUpdated(setPassportsState, item)
    })

    const removePassportSchemaSub = eventBus.sub('mdm.passports.deleted', (id) => {
      recoilStateUpdaters.itemDeleted(setPassportsState, id)
    })

    const createPassportSchemaGroupSub = eventBus.sub('mdm.passports.groups.created', (item) => {
      setPassportsState((prev) =>
        produce(prev, (draft: IPassportSchemasAtomState) => {
          const passportIndex = draft.items.findIndex((it) => it.id === item.passportSchemaId)
          if (passportIndex === -1) return

          draft.items[passportIndex].groups.push(item)
          return draft
        })
      )
    })

    const updatePassportSchemaGroupSub = eventBus.sub('mdm.passports.groups.updated', (item) => {
      setPassportsState((prev) =>
        produce(prev, (draft: IPassportSchemasAtomState) => {
          const passportIndex = draft.items.findIndex((it) => it.id === item.passportSchemaId)
          if (passportIndex === -1) return

          const groupIndex = draft.items[passportIndex].groups.findIndex((it) => it.id === item.id)
          if (groupIndex === -1) return

          draft.items[passportIndex].groups[groupIndex] = { ...draft.items[passportIndex].groups[groupIndex], ...item }
          return draft
        })
      )
    })

    const removePassportSchemaGroupSub = eventBus.sub('mdm.passports.groups.deleted', (id) => {
      setPassportsState((prev) =>
        produce(prev, (draft: IPassportSchemasAtomState) => {
          const passportIndex = draft.items.findIndex((it) => it.groups.some((g) => g.id === id))
          if (passportIndex === -1) return

          draft.items[passportIndex].groups = draft.items[passportIndex].groups.filter((g) => g.id !== id)
          return draft
        })
      )
    })

    return () => {
      createEnumsSub()
      updateEnumsSub()
      removeEnumsSub()

      createUnitsSub()
      updateUnitsSub()
      removeUnitsSub()

      createUnitInstanceSub()
      updateUnitInstanceSub()
      removeUnitInstanceSub()

      createPassportSchemaSub()
      updatePassportSchemaSub()
      removePassportSchemaSub()

      createPassportSchemaGroupSub()
      updatePassportSchemaGroupSub()
      removePassportSchemaGroupSub()
    }
  }, [])
}

export default useMDMEventsSubscriptions
